import React, {
  useEffect, useRef, useMemo
} from 'react'

import { PHONE_NUMBER_STATE, updatePhoneNumberState } from '@apps/redux/features/PhoneNumberSlice'
import { useDispatch, useSelector } from 'react-redux'
import { getById, getBySlug, getImgByTitle } from '@apps/utils/contentful'
import Sites from '@sites/index'
import { Input, Spinner } from '@telus/koodo-ui-kit'

const PhoneNumberV2 = ({ handleBlurPhoneNumber }) => {
  const dispatch = useDispatch()
  const phoneNumberState = useSelector((state) => state.phoneNumberPage)
  const areaCodeRef = useRef(null)

  const {
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_AREA_CODE]: areaPincode,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_THREE_DIGITS]: threeDigits,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_FOUR_DIGITS]: fourDigits,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER]: phoneNumber,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PREV_PORT_IN_PHONE_NUMBER]: prevPhoneNumber,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER
      .IS_PREV_PORT_IN_NUMBER_ELIGIBLE]: isPrevPortInNumberEligible,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER_HAS_ERROR]: hasError,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER_ERROR_MESSAGE]: errorMessage,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER_IS_LOADING]: isLoading
  } = phoneNumberState

  const {
    content: { pageContent },
    user: { selectedLanguage },
    userType
  } = useSelector((state) => state.common)

  const isDealer = userType === Sites.dealer
  const pagePrefix = isDealer ? 'Dealer' : 'Consumer'
  const textContent = useMemo(() => {
    const commonElements = getById(pageContent, 'commonElements')[selectedLanguage]
    const phoneNumberPageContent = getById(pageContent, `phoneNumberPage${pagePrefix}`) || {}
    const { shortTextFields } = phoneNumberPageContent[selectedLanguage] || {}

    return {
      phoneInvalid: getBySlug(shortTextFields, 'phoneInvalid')?.value,
      phoneRequired: getBySlug(shortTextFields, 'phoneRequired')?.value,
      validCanadianNumber: getBySlug(shortTextFields, 'validCanadianNumber')?.value,
      currentPhoneNumberLabel: getBySlug(shortTextFields, 'currentPhoneNumberLabel')?.value,
      error: getImgByTitle(commonElements, 'error')?.url
    }
  }, [pageContent, selectedLanguage])

  const { phoneInvalid, phoneRequired, validCanadianNumber, currentPhoneNumberLabel } = textContent

  const updatePhoneNumberStore = (payload) => {
    dispatch(updatePhoneNumberState(payload))
  }

  const formattedPhoneNumber = (rawNumber) => {
    const phoneArr = []
    const chunk = rawNumber.substring(0, 3)

    if (chunk.length > 0) {
      phoneArr.push(chunk)
    }

    const chunk1 = rawNumber.substring(3, 6)

    if (chunk1.length > 0) {
      phoneArr.push(chunk1)
    }

    const chunk2 = rawNumber.substring(6, 10)

    if (chunk2.length > 0) {
      phoneArr.push(chunk2)
    }

    if (phoneArr && phoneArr.length > 0) {
      return phoneArr.join(' ')
    }

    return ''
  }

  const updateField = (payload) => {
    const rawValue =
      typeof payload === 'string'
        ? payload
        : payload?.target?.value || payload?.value || ''
    const digitsOnly = rawValue.replace(/\D/g, '').slice(0, 10)
    const areaCode = digitsOnly.substring(0, 3)
    const middleDigits = digitsOnly.substring(3, 6)
    const lastDigits = digitsOnly.substring(6, 10)

    updatePhoneNumberStore({
      [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_AREA_CODE]: areaCode,
      [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_THREE_DIGITS]: middleDigits,
      [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_FOUR_DIGITS]: lastDigits,
      [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER]: digitsOnly,
      [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.IS_PORT_IN_AGREEMENT_CHECKED]: false,
      [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.IS_PORT_IN_NUMBER_ELIGIBLE]:
        digitsOnly === prevPhoneNumber ? isPrevPortInNumberEligible : false
    })
  }
  useEffect(() => {
    if (areaPincode || threeDigits || fourDigits) validatePhoneNumber()
  }, [areaPincode, threeDigits, fourDigits])

  const validatePhoneNumber = () => {
    if (phoneNumber) {
      if (phoneNumber.length === 0) {
        updatePhoneNumberStore({
          [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER_HAS_ERROR]: true,
          [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER_ERROR_MESSAGE]: phoneRequired
        })
      }
      if (phoneNumber.length > 0 && phoneNumber.length < 10) {
        updatePhoneNumberStore({
          [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER_HAS_ERROR]: true,
          [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER_ERROR_MESSAGE]: phoneInvalid
        })
      }
      if (phoneNumber.length === 10) {
        if (prevPhoneNumber !== phoneNumber || isPrevPortInNumberEligible) {
          updatePhoneNumberStore({
            [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER_HAS_ERROR]: false,
            [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER_ERROR_MESSAGE]: ''
          })
        } else {
          updatePhoneNumberStore({
            [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER_HAS_ERROR]: true,
            [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER
              .PORT_IN_PHONE_NUMBER_ERROR_MESSAGE]: validCanadianNumber
          })
        }
      }
    }
  }

  const onPhoneBlur = async () => {
    const blurHasError = await handleBlurPhoneNumber()
    if (blurHasError) {
      areaCodeRef.current.scrollIntoView({
        behavior: 'smooth'
      })
    }
  }

  return (
    <>
      <div className="flex flex-row flex-wrap items-center content-between justify-between lg:justify-start">
        <Input
          ref={areaCodeRef}
          label={currentPhoneNumberLabel}
          onBlur={onPhoneBlur}
          onChange={updateField}
          onClear={() => updatePhoneNumberStore({
            [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_AREA_CODE]: '',
            [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_THREE_DIGITS]: '',
            [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_FOUR_DIGITS]: '',
            [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.PORT_IN_PHONE_NUMBER]: '',
            [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.IS_PORT_IN_AGREEMENT_CHECKED]: false,
            [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.IS_PORT_IN_NUMBER_ELIGIBLE]: false
          })}
          onFocus={() => { }}
          onKeyDown={() => { }}
          onSubmit={() => { }}
          placeholder="(XXX) XXX XXXX"
          type="tel"
          value={formattedPhoneNumber(phoneNumber)}
          disabled={isLoading}
          feedback={errorMessage}
          validation={hasError ? 'error' : undefined}
          maxLength="12"
          data-testid="phoneNumber-transferPhoneNumber"
        />

        {isLoading && <div className="mt-2 ml-3">
          <Spinner show={isLoading} variant={{ size: 'small' }} />
        </div>}
      </div>
    </>
  )
}

export default PhoneNumberV2
